import React from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
} from "@mui/material";
import {
  BIBLE_CATEGORIES_ZH,
  BIBLE_CATEGORIES_EN,
  getBookAbbreviation,
} from "../../bible/bibleCategories";

const PanoramicCanonView = ({
  onSelectBook,
  edition,
  readChapterMap = {},
  bibleChapters = [],
}) => {
  const isEn = edition?.id === "en";
  const categories = isEn ? BIBLE_CATEGORIES_EN : BIBLE_CATEGORIES_ZH;

  // Build map of bookName -> chapterCount & readCount
  const bookStats = React.useMemo(() => {
    const stats = {};
    bibleChapters.forEach((ch) => {
      const raw = ch.bookName || "";
      const book = raw.includes("-") ? raw.split("-").slice(1).join("-") : raw;
      if (!stats[book]) {
        stats[book] = { total: 0, read: 0 };
      }
      stats[book].total += 1;
      if (readChapterMap[ch.id]) {
        stats[book].read += 1;
      }
    });
    return stats;
  }, [bibleChapters, readChapterMap]);

  const renderSection = (title, catList) => (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          color: "primary.main",
          mb: 2,
          pb: 0.75,
          borderBottom: "2px solid",
          borderColor: "primary.main",
          display: "inline-block",
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {catList
          .filter((cat) => cat.books && cat.books.length > 0)
          .map((cat) => (
            <Paper
              key={cat.id}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 800, color: "text.primary" }}
                >
                  {cat.label}
                </Typography>
                <Chip
                  label={`${cat.books.length} ${isEn ? "books" : "卷"}`}
                  size="small"
                  sx={{ height: 20, fontSize: "0.7rem" }}
                />
              </Box>

              <Grid container spacing={1}>
                {cat.books.map((book) => {
                  const bStat = bookStats[book] || { total: 0, read: 0 };
                  const isCompleted =
                    bStat.total > 0 && bStat.read >= bStat.total;
                  const abbr = getBookAbbreviation(book);

                  return (
                    <Grid item xs={6} sm={4} md={3} lg={2.4} key={book}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => onSelectBook(book)}
                        sx={{
                          py: 1,
                          px: 1.25,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          textAlign: "left",
                          textTransform: "none",
                          borderColor: isCompleted
                            ? "success.main"
                            : "divider",
                          bgcolor: isCompleted
                            ? (theme) =>
                                theme.palette.mode === "dark"
                                  ? "rgba(34, 197, 94, 0.08)"
                                  : "rgba(34, 197, 94, 0.04)"
                            : "transparent",
                          "&:hover": {
                            borderColor: "primary.main",
                            bgcolor: "action.hover",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: 1,
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.75rem",
                              fontWeight: 800,
                              flexShrink: 0,
                            }}
                          >
                            {abbr}
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              color: "text.primary",
                            }}
                          >
                            {book}
                          </Typography>
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5, flexShrink: 0 }}
                        >
                          {bStat.total}
                          {isEn ? "ch" : "章"}
                        </Typography>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          ))}
      </Box>
    </Box>
  );

  return (
    <Box>
      {renderSection(
        isEn ? "Old Testament (39 Books)" : "旧约全书 (39卷)",
        categories.ot
      )}
      {renderSection(
        isEn ? "New Testament (27 Books)" : "新约全书 (27卷)",
        categories.nt
      )}
    </Box>
  );
};

export default PanoramicCanonView;
